import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import { config } from '@keytrade/functions';
import { Icon } from '@keytrade/components-icon';

import Container from '@/components/Container';
import BlockWrapper from '@/components/BlockWrapper';
import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';
import Link, { LinkStyle, LinkType } from '@/components/Link';
import { isDarkTheme } from '@/utils/StyleUtils';
import { mediaQuery } from '@/utils/helpers';

const { list: colors } = config.colors;

type BenefitsType = {
  icon: string;
  iconBackgroundColor: string;
  title: string;
  textString: {
    text: string;
  };
};

type Props = {
  id: string;
  backgroundColor: string;
  title: string;
  subtitleString: {
    subtitle: string;
  };
  benefits: Array<BenefitsType>;
  enableIconColors: boolean;
  primaryLink: LinkType;
  secondaryLink: LinkType;
};

const Header = styled.div`
  text-align: center;
  margin-bottom: 3.2rem;

  ${mediaQuery.md(`
    margin-bottom: 8.8rem;
  `)};
`;

const BenefitsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;

  ${mediaQuery.md(`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  `)};
`;

const IconWrapper = styled.div`
  height: 5.6rem;
  width: 5.6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2.4rem 0 2rem;
  background-color: ${({ color }) => colors[color]};
  border-radius: 50%;
`;

const CTAWrapper = styled.div`
  width: 100%;
  margin: 4.8rem -1rem -1rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  > * {
    margin: 1rem;
  }

  ${mediaQuery.md(`
    margin-top: 9.6rem;
  `)};
`;

const IconBenefitsBlock: React.FC<Props> = ({
  id,
  backgroundColor,
  title,
  subtitleString,
  benefits,
  enableIconColors,
  primaryLink,
  secondaryLink,
}) => {
  let defaultIconBackground;
  switch (backgroundColor) {
    case 'White':
      defaultIconBackground = 'BlueLight';
      break;
    case 'Light blue':
    case 'Blue gradient':
      defaultIconBackground = 'White';
      break;
    case 'Dark blue':
    case 'Dark blue gradient':
      defaultIconBackground = 'BlueDark';
      break;
  }

  return (
    <BlockWrapper id={id} color={backgroundColor}>
      <Container>
        {(title || subtitleString?.subtitle) && (
          <Header>
            {title && (
              <Title
                size='xl'
                level='h2'
                margin='0 0 2rem'
                lightColor={isDarkTheme(backgroundColor)}
              >
                {title}
              </Title>
            )}
            {subtitleString?.subtitle && (
              <Paragraph
                color={isDarkTheme(backgroundColor) ? 'White' : 'GreyBlue'}
                margin='0 0 1.5rem'
                weight='semiBold'
              >
                {subtitleString.subtitle}
              </Paragraph>
            )}
          </Header>
        )}
        <BenefitsWrapper>
          {benefits?.map((benefit) => (
            <div key={benefit.title}>
              <IconWrapper
                color={
                  enableIconColors
                    ? benefit.iconBackgroundColor
                    : defaultIconBackground
                }
              >
                <Icon
                  icon={benefit.icon}
                  color={enableIconColors ? colors.White : colors.BlueSky}
                  size='4rem'
                />
              </IconWrapper>
              <Title
                size='md'
                level='h3'
                margin='0 0 1.6rem'
                lightColor={isDarkTheme(backgroundColor)}
              >
                {benefit.title}
              </Title>
              <Paragraph
                color={isDarkTheme(backgroundColor) ? 'White' : 'GreyBlue'}
              >
                {benefit.textString.text}
              </Paragraph>
            </div>
          ))}
        </BenefitsWrapper>
        {(primaryLink || secondaryLink) && (
          <CTAWrapper>
            <Link {...primaryLink} linkStyle={LinkStyle.PRIMARY} />
            <Link {...secondaryLink} linkStyle={LinkStyle.SECONDARY} />
          </CTAWrapper>
        )}
      </Container>
    </BlockWrapper>
  );
};

export default IconBenefitsBlock;

export const iconBenefitsBlockFragmentQuery = graphql`
  fragment IconBenefitsBlockFragment on ContentfulIconBenefitsBlock {
    id
    __typename
    title
    subtitleString: subtitle {
      subtitle
    }
    benefits {
      icon
      iconBackgroundColor
      title
      textString: text {
        text
      }
    }
    enableIconColors
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }
    backgroundColor
  }
`;
